import { AppDisplay } from '@ui/applications/AppDisplay';
import { ColumnType } from 'antd/es/table';
import { ComplianceCategories } from '@ui/icons/ComplianceCategories';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { DataSecurityEvent } from '@sonar-software/sonar-core';
import { DataSensitivityLevelTag } from '@ui/tags/DataSensitivityLevelTag';
import { getTimeRecordDate } from '@sonar-software/sonar-ts-lib';
import React from 'react';
import { SonarText } from '@ui/text/SonarText';
import { Tooltip } from 'antd';

type ColumnDefinition = ColumnType<DataSecurityEvent> & { dataIndex: keyof DataSecurityEvent };

export const fieldColumn: ColumnDefinition = {
	dataIndex: 'fieldName',
	fixed: true,
	key: 'fieldName',
	render: (_, { fieldName, dataSensitivityLevelId }) =>
		<div className='field-column'>
			<Tooltip
				open={ fieldName.length > 35 || dataSensitivityLevelId ? undefined : false }
				title={ fieldName }>
				<SonarText>{ fieldName }</SonarText>
			</Tooltip>
			<DataSensitivityLevelTag
				dataSensitivityLevelId={ dataSensitivityLevelId } />
		</div>,
	title: COPY_SPECIFIC.FIELD,
	width: 120
};

export const parentColumn: ColumnDefinition = {
	dataIndex: 'parentName',
	key: 'parentName',
	render: (parentName: string) =>
		<Tooltip
			mouseEnterDelay={ 0.5 }
			open={ parentName.length > 35 ? undefined : false }
			title={ parentName }>
			<SonarText>{ parentName }</SonarText>
		</Tooltip>,
	title: COPY_SPECIFIC.PARENT_OBJECT,
	width: 120
};

export const dateColumn: ColumnDefinition = {
	align: 'right',
	dataIndex: 'eventDate',
	key: 'eventDate',
	render: (date: string) => <SonarText>{ getTimeRecordDate(date) }</SonarText>,
	sorter: true,
	title: COPY_SPECIFIC.EVENT_DATE,
	width: 120
};

export const usernameColumn: ColumnDefinition = {
	dataIndex: 'username',
	key: 'username',
	render: (username) =>
		<Tooltip
			mouseEnterDelay={ 0.5 }
			open={ username.length > 35 ? undefined : false }
			title={ username }>
			<SonarText>{ username }</SonarText>
		</Tooltip>,
	title: COPY_SPECIFIC.USERNAME,
	width: 120
};

export const classificationColumn: ColumnDefinition = {
	className: 'classification-column',
	dataIndex: 'complianceCategory',
	key: 'complianceCategory',
	render: (complianceCategory) => <ComplianceCategories
		categoryFlags={ complianceCategory }
		direction='horizontal' />,
	title: COPY_SPECIFIC.CLASSIFICATION,
	width: 120
};

export const apiVersionColumn: ColumnDefinition = {
	align: 'right',
	dataIndex: 'apiVersion',
	key: 'apiVersion',
	title: COPY_SPECIFIC.API_VERSION,
	width: 100
};

export const eventIdColumn: ColumnDefinition = {
	className: 'event-identifier-column',
	dataIndex: 'eventId',
	key: 'eventId',
	title: COPY_SPECIFIC.EVENT_IDENTIFIER,
	width: 100
};

export const loginHistoryIdColumn: ColumnDefinition = {
	dataIndex: 'loginHistoryId',
	key: 'loginHistoryId',
	title: COPY_SPECIFIC.LOGIN_HISTORY_ID,
	width: 120
};

export const policyIdColumn: ColumnDefinition = {
	align: 'right',
	dataIndex: 'policyId',
	key: 'policyId',
	title: COPY_SPECIFIC.POLICY_ID,
	width: 100
};

export const policyOutcomeColumn: ColumnDefinition = {
	dataIndex: 'policyOutcome',
	key: 'policyOutcome',
	title: COPY_SPECIFIC.POLICY_OUTCOME,
	width: 150
};

export const operationColumn: ColumnDefinition = {
	dataIndex: 'operation',
	key: 'operation',
	title: COPY_SPECIFIC.OPERATION,
	width: 100
};

export const sourceIpColumn: ColumnDefinition = {
	dataIndex: 'sourceIp',
	key: 'sourceIp',
	title: COPY_SPECIFIC.SOURCE_IP,
	width: 100
};

export const userAgentColumn: ColumnDefinition = {
	dataIndex: 'userAgent',
	key: 'userAgent',
	title: COPY_SPECIFIC.USER_AGENT,
	width: 100
};

export const userIdColumn: ColumnDefinition = {
	dataIndex: 'userId',
	key: 'userId',
	title: COPY_SPECIFIC.USER_ID,
	width: 100
};

export const countryIsoColumn: ColumnDefinition = {
	dataIndex: 'countryIso',
	key: 'countryIso',
	title: COPY_SPECIFIC.COUNTRY_ISO,
	width: 100
};

export const appColumn: ColumnDefinition = {
	dataIndex: 'applicationId',
	key: 'applicationId',
	render: (applicationId) => <AppDisplay appId={ applicationId } />,
	title: COPY_SPECIFIC.APPLICATION,
	width: 170
};

export function getColumns(): Array<ColumnDefinition> {
	return [
		fieldColumn,
		parentColumn,
		dateColumn,
		usernameColumn,
		classificationColumn,
		apiVersionColumn,
		eventIdColumn,
		loginHistoryIdColumn,
		policyIdColumn,
		policyOutcomeColumn,
		operationColumn,
		sourceIpColumn,
		userAgentColumn,
		userIdColumn,
		countryIsoColumn,
		appColumn
	];
}
